import { Link } from "@mui/material";

import { faqs, howItWorks } from "./helpers";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

import { routes } from "@/global/routes";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";

import type { TeamConfig } from "../types";

import logo from "public/images/calgary/flex_club_logo.png";
import AuthBackgroundBanner from "public/images/calgary/background.webp";
import SeatMap3dPreviewImage from "public/images/calgary/background.webp";
import FindGamesBanner from "public/images/calgary/FlexClub_Purchasing_Platform.jpg";

const config: TeamConfig = {
  teamSchema: "calgary_flexbank",
  browserTitle: "FlexClub",
  packages: false,
  teamName: "Calgary Flames",

  manageCard: false,

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.giftCard,
    hideRedeemItem: true,
    hideRecipient: true,
  },

  // disabled routes
  disabledRoutes: [],

  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-ca-10010-flames-sec",
    mapId: "blockmap2d",
  },

  checkoutCreditPackages: false,
  availableCreditEnabled: true,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.seatLevelMulti,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    allowManualCartInSimilar: false,
    showFrontendTotalAmount: false,
    showFrontendServiceFee: false,
    checkoutTimerEnabled: false,
    isPerksEnabled: false,
    defaultSeatCount: 2,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: true,
    showPurchaseCreditLinkInHomeMenu: true,
    isPurchaseCreditHistoryEnabled: true,
    checkoutTimerEnabled: true,
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,

  isBrainTreePaymentEnabled: false,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "G-KQKLSP531X",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "",
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl:
      "https://flex.attendevents.com/calgary_flexbank/events_home",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/flames/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  images: {
    logoNameImage: "",
    faviconImage: logo.src,
    logoImage: logo.src + "?width=84&height=66",
    findGamesBannerImage: FindGamesBanner.src,
    authBackgroundImage: AuthBackgroundBanner.src,
    seatMap3dPreviewImage: SeatMap3dPreviewImage.src,
    checkoutStatusPageImage: AuthBackgroundBanner.src,
    giftCardStandalonePageImage: AuthBackgroundBanner.src,
  },

  colors: {
    primary: "#D02030",
  },

  howItWorks,
  faqs,

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      hideOverlayLogo: false,
      showLogoWithTitle: false,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    hideCheckoutCartBalance: false,

    purchaseCredit: {
      hideSecondaryTitle: true,
      purchaseCreditTitle: "Purchase Credit",
    },

    gameFiltersSettings: {
      dynamicSeatCount: [
        {
          id: 1,
          title: "Adult",
          description: null,
          ticketTypeCode: "_W1",
          seatCount: 2,
          pricingLabel: "Adult Price:",
          orderIndex: 1,
        },
        {
          id: 2,
          title: "Child/Student Ticket",
          description: "Age 2-17/Post Secondary (ID Required)",
          ticketTypeCode: "_W2",
          seatCount: 0,
          pricingLabel: "Child/Student:",
          orderIndex: 2,
        },
      ],
    },

    customAdaLabel: (
      <>
        If you need accessible seats, please contact the Box Office at{" "}
        <Link
          href="tel:4037774646"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (403) 777-4646
        </Link>
        .
      </>
    ),
  },

  languages: {
    topNavMenuItems: <></>,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseGiftCard: null,
    redeemGiftCard: null,
    purchaseSeats: {
      progressBarText: "Select 1 event to continue!",
      progressBarSuccessText:
        "You're good to go! Add more events or select Continue to view seating options.",
    },
    purchaseCredit: {
      mainCardDescription:
        "A customizable spending account that allows you to utilize your tickets in a flexible way that best suits your needs.",
    },

    gameFilters: {
      category: {
        title: "Team",
        description: "Team",
      },
    },

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your FlexClub tickets are confirmed! You can use Flames Account Manager or Calgary Flames App to access tickets.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
